import { Avatar, Chip, Typography } from '@mui/material';
import CurrencyFormat from 'react-currency-format';
import RenderChip from 'ui-component/RenderChip';
import {
    IconDashboard,
    IconBuildingBank,
    IconCoins,
    IconChartBar,
    IconHierarchy,
    IconBuildingBridge,
    IconBuildingCommunity,
    IconTractor,
    IconBucket,
    IconPlug,
    IconSchool,
    IconUsers,
    IconShield,
    IconHeart,
    IconFlame,
    IconDeviceDesktop
} from '@tabler/icons-react';

export const fundIconMap = {
    'Community Development Special Fund ': IconCoins,
    'Regional Development Fund ': IconChartBar,
    'Capital Projects': IconBuildingBank,
    'Education (MOET)': IconSchool,
    'World Bank': IconBuildingBank
};

export const iconSectorMap = {
    'Transport & Commuinication': IconBuildingBridge,
    'Transport & Communication': IconBuildingBridge,

    'Community Centre': IconBuildingCommunity,
    'Community Centres': IconBuildingCommunity,

    Agriculture: IconTractor,
    'Water & Sanitation': IconBucket,
    'Rural Electrification': IconPlug,
    Education: IconSchool,

    Health: IconHeart,
    Defence: IconShield,
    ICT: IconDeviceDesktop,
    Finance: IconCoins,
    'Royal Eswatini Police': IconShield,
    'Eswatini Fire Department': IconFlame,
    'Various Other Ministries': IconHierarchy
};

export const departments = ['civils', 'accounting', 'procurement', 'qs', 'architecture', 'water', 'electrical'];

const pdfView = (url) => {
    const pdfWindow = window.open();
    pdfWindow.location.href = url;
};

export const fmtCurrency = (n) =>
    n != null
        ? `E ${Number(n).toLocaleString('en-SZ', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
        : 'E 0.00';

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export const fmtDateTime = (d) =>
    d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const fmtFileSize = (bytes) => {
    if (!bytes) return '';
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
};

export const ROLE_COLORS = {
    super_admin: { bg: '#fde8e8', color: '#991b1b', border: '#fca5a5' },
    system_admin: { bg: '#fce8d5', color: '#9a3412', border: '#fdba74' },
    department_head: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    project_manager: { bg: '#e0f2fe', color: '#075985', border: '#7dd3fc' },
    quantity_surveyor: { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd' },
    procurement_officer: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    accounting_officer: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
    technical_support: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    guest: { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' }
};

export const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_qs_review', label: 'Pending QS Review' },
    { value: 'qs_approved', label: 'QS Approved' },
    { value: 'pending_procurement', label: 'Pending Procurement' },
    { value: 'procurement_rejected', label: 'Rejected - Procurement' },
    { value: 'procurement_approved', label: 'Procurement Approved' },
    { value: 'pending_accounting', label: 'Pending Accounting' },
    { value: 'accounting_approved', label: 'Accounting Approved' },
    { value: 'checking', label: 'Checking' },
    { value: 'at_treasury', label: 'At Treasury' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' }
];

export const STATUS_COLORS = {
    draft: 'default',
    pending_qs_review: 'warning',
    qs_approved: 'info',
    pending_procurement: 'warning',
    procurement_rejected: 'error',
    procurement_approved: 'info',
    pending_accounting: 'warning',
    accounting_approved: 'info',
    checking: 'warning',
    at_treasury: 'secondary',
    paid: 'success',
    overdue: 'error'
};

export const STATUS_LABELS = {
    draft: 'Draft',
    pending_qs_review: 'QS Review',
    qs_approved: 'QS Approved',
    pending_procurement: 'Procurement',
    procurement_review: 'Procurement Review',
    pending_procurement_head_approval: 'Proc. H.O.D Approval',
    procurement_rejected: 'Rejected (Proc.)',
    procurement_approved: 'Proc. Approved',
    pending_accounting: 'Accounting',
    accounting_review: 'Accounting Review',
    pending_accounting_head_approval: 'Acct. H.O.D Approval',
    accounting_rejected: 'Rejected (Acct.)',
    accounting_approved: 'Acct. Approved',
    checking: 'Checking',
    at_treasury: 'Treasury',
    paid: 'Paid',
    overdue: 'Overdue'
};

/**
 * Statuses the PROCUREMENT department sees.
 * Mirrors Certificate::scopeVisibleTo('procurement') on the backend.
 */
export const PROCUREMENT_STATUSES = [
    'qs_approved',
    'pending_procurement',
    'procurement_review',
    'pending_procurement_head_approval',
    'procurement_rejected',
    'procurement_approved',
    'pending_accounting',
    'accounting_rejected',
    'accounting_approved',
    'checking',
    'at_treasury',
    'paid'
];

/**
 * Statuses the ACCOUNTING department sees.
 * Mirrors Certificate::scopeVisibleTo('accounting') on the backend.
 */
export const ACCOUNTING_STATUSES = ['procurement_approved', 'pending_accounting', 'accounting_review', 'pending_accounting_head_approval', 'accounting_rejected', 'checking', 'at_treasury', 'paid'];

/**
 * Statuses shown on the TREASURY / PAYMENTS page.
 */
export const TREASURY_STATUSES = ['at_treasury', 'paid'];

/** Returns MUI colour tokens for each status */
export function statusColor(status) {
    const map = {
        draft: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
        pending_qs_review: { bg: '#fef9c3', text: '#854d0e', border: '#fde047' },
        qs_approved: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
        pending_procurement: { bg: '#fff7ed', text: '#9a3412', border: '#fdba74' },
        procurement_rejected: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
        procurement_approved: { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
        pending_accounting: { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
        accounting_rejected: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
        accounting_approved: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
        at_treasury: { bg: '#f0fdf4', text: '#14532d', border: '#bbf7d0' },
        paid: { bg: '#f0fdf4', text: '#166534', border: '#86efac' },
        overdue: { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5' }
    };
    return map[status] ?? map.draft;
}

export const phcolumns = [];

export const columns = [
    {
        title: 'Ranking',
        render: (rowData) => <span>{rowData.tableData.id + 1}</span>
    },
    {
        title: 'Company',
        field: 'supplier_id'
    },
    {
        title: 'Cost',
        field: 'cost'
    }
];

export const rmcolumns = [
    {
        title: 'Ranking',
        field: 'id',
        render: (rowData) => <span>{rowData.tableData.id + 1}</span>
    },
    {
        title: 'Company',
        field: 'supplier_id'
    },
    {
        title: 'Cost/25MPa m3',
        field: 'mpa_cost'
    },
    {
        title: 'Trans. Cost/km',
        field: 'trans_cost'
    },
    {
        title: 'Ranking Cost*',
        field: 'rank_cost'
    }
];

export const TbColumns = [
    {
        title: 'Project No.',
        field: 'project_number'
    },
    {
        title: 'Name of Project',
        field: 'name'
    },
    {
        title: 'Inkhundla',
        field: 'inkhundla'
    },
    {
        title: 'Contract-Sum',
        field: 'contract_sum',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat
                    value={row.contract_sum}
                    displayType="text"
                    thousandSeparator
                    prefix="E"
                    decimalScale={2}
                    fixedDecimalScale
                />
            </Typography>
        )
    },
    {
        title: 'Contract Price',
        field: 'actual_sum',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat value={row.actual_sum} decimalScale={2} fixedDecimalScale displayType="text" thousandSeparator prefix="E" />
            </Typography>
        )
    },
    {
        title: 'Retention',
        field: 'retention',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat value={row.retention} decimalScale={2} fixedDecimalScale displayType="text" thousandSeparator prefix="E" />
            </Typography>
        )
    },
    {
        title: 'Expenditure (To Date)',
        field: 'expenditure_to_date',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat
                    decimalScale={2}
                    fixedDecimalScale
                    value={row.expenditure_to_date}
                    displayType="text"
                    thousandSeparator
                    prefix="E"
                />
            </Typography>
        )
    },
    {
        title: 'Amount Outstanding ',
        field: 'amount_outstanding',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat
                    decimalScale={2}
                    fixedDecimalScale
                    value={row.amount_outstanding}
                    displayType="text"
                    thousandSeparator
                    prefix="E"
                />
            </Typography>
        )
    },
    {
        title: 'Intended Direct Beneficiaries',
        field: 'intended_beneficiaries'
    },
    {
        title: 'Actual Direct Beneficiaries',
        field: 'actual_beneficiaries'
    },
    {
        title: 'Intended Indirect Beneficiaries',
        field: 'intended_indirect_beneficiaries'
    },
    {
        title: 'Actual Indirect Beneficiaries',
        field: 'actual_indirect_beneficiaries'
    },
    {
        title: 'Beneficiary Type',
        field: 'beneficiary_type'
    },
    {
        title: 'Commencement Date',
        field: 'comencement_date',
        render: (row) => fmtDate(row.comencement_date)
    },
    {
        title: 'Completion Date',
        field: 'completion_date',
        render: (row) => fmtDate(row.completion_date)
    },
    {
        title: 'Revised Completion Date',
        field: 'revised_completion_date',
        render: (row) => fmtDate(row.revised_completion_date)
    },
    {
        title: 'Scope',
        field: 'scope',
        render: (row) => (
            <Typography
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                }}
            >
                {row.scope}
            </Typography>
        )
    },
    {
        title: 'Description',
        field: 'description',
        render: (row) => (
            <Typography
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                }}
            >
                {row.description}
            </Typography>
        )
    },
    {
        title: 'Remaining Works',
        field: 'remaining_works',
        render: (row) => (
            <Typography
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                }}
            >
                {row.remaining_works}
            </Typography>
        )
    },
    {
        title: 'Progress Narrative',
        field: 'progress_narrative',
        render: (row) => (
            <Typography
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                }}
            >
                {row.progress_narrative}
            </Typography>
        )
    },
    {
        title: 'Completion Certificate',
        field: 'completion_certificate'
    },
    {
        title: 'Status',
        field: 'status',
        lookup: {
            'in-progress': 'In progress',
            complete: 'Completed',
            waiting: 'To be launched',
            deferred: 'Deferred',
            cancelled: 'Cancelled',
            dlp: 'DLP'
        },
        render: (rowData) => <RenderChip val={rowData.status} />
    },
    {
        title: 'Created at',
        field: 'created_at',
        render: (row) => fmtDate(row.created_at)
    },
    {
        title: 'Updated at',
        field: 'updated_at',
        render: (row) => fmtDate(row.updated_at)
    }
];

export const ProjectNumberColumns = [
    {
        title: 'Project No.',
        field: 'project_number'
    },
    {
        title: 'Name of Project',
        field: 'name'
    },
    {
        title: 'Contract-Sum',
        field: 'contract_sum',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat
                    value={row.contract_sum}
                    displayType="text"
                    thousandSeparator
                    prefix="E"
                    decimalScale={2}
                    fixedDecimalScale
                />
            </Typography>
        )
    },
    {
        title: 'Created at',
        field: 'created_at',
        render: (row) => fmtDate(row.created_at)
    },
    {
        title: 'Updated at',
        field: 'updated_at',
        render: (row) => fmtDate(row.updated_at)
    }
];

export const MPColumns = [
    {
        title: 'Project No.',
        field: 'project_number'
    },
    {
        title: 'Name of Project',
        field: 'name'
    },
    {
        title: 'Inkhundla',
        field: 'inkhundla'
    },
    {
        title: 'Status',
        field: 'status',
        render: (rowData) => rowData.status === 'in-progress' && <Chip label="In progress" color="primary" />
    }
];

export const dbColumns = [
    {
        title: 'Company Name',
        field: 'company_name'
    },
    {
        title: 'Phone',
        field: 'phone',
        render: (row) => <Typography sx={{ width: '140px' }}>{row.phone}</Typography>
    },
    {
        title: 'Email',
        field: 'email'
    },
    {
        title: 'License Type',
        field: 'license_type'
    },
    {
        title: 'Acc NO.',
        field: 'account_number',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat value={row.account_number} displayType="text" format="#### #### #### ####" />
            </Typography>
        )
    },
    {
        title: 'Physical Address',
        field: 'physical_address'
    },
    {
        title: 'Postal Code',
        field: 'postal_code'
    },
    {
        title: 'Date Created',
        field: 'created_at',
        editable: 'never',
        render: (row) => fmtDate(row.created_at)
    }
];
export const reportColumns = [
    {
        title: 'User',
        field: 'user',
        render: (rowData) => <Typography>{`${rowData.user.fname}     ${rowData.user.lname}`}</Typography>
    },
    {
        title: 'Project',
        field: 'project.name'
    },

    {
        title: 'Date Reported',
        field: 'created_at',
        render: (row) => fmtDate(row.created_at)
    }
];

export const frcolumns = [
    {
        title: 'Reported By',
        field: 'full_name'
    },
    {
        title: 'Project',
        field: 'name'
    },

    {
        title: 'Date Reported',
        field: 'created_at',
        render: (row) => fmtDate(row.created_at)
    }
];

export const pcolumns = [
    { field: 'name_of_organization', title: 'Name of Organization' },
    { field: 'name_of_representative', title: 'Name of Representative' },
    { field: 'position', title: 'Position' }
];
export const wcolumns = [
    { field: 'targets', title: 'Targets' },
    { field: 'progress', title: 'Progress' },
    { field: 'percentage_work_done', title: '% Work Done' }
];

export const ecolumns = [
    { title: 'Year', field: 'year' },
    { title: 'Programme', field: 'programme' },
    { title: 'Certificate', field: 'certificate' },
    { title: 'Institution', field: 'institution' }
];

export const emcolumns = [
    { title: 'Year', field: 'year' },
    { title: 'Position', field: 'position' },
    { title: 'Occupation', field: 'occupation' },
    { title: 'Tasks', field: 'tasks' }
];
export const ucolumns = [
    { title: 'Image', field: 'photo', render: (row) => <Avatar src={row?.details?.photo} alt="" /> },
    { title: 'Name', field: 'fname' },
    { title: 'Surname', field: 'lname' }
];
export const rcolumns = [
    {
        title: 'Project',
        field: 'name',
        render: (row) => (
            <Typography
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                }}
            >
                {row.name}
            </Typography>
        )
    },
    { title: 'Reported By', field: 'full_name' },
    { title: 'Date', field: 'created_at', render: (row) => fmtDate(row.created_at) }
];

export const contractColumn = [
    { title: 'Project Name', field: 'name' },
    { title: 'Contract Type', field: 'type' },
    {
        title: 'Contract Budget',
        field: 'budget',
        render: (row) => (
            <Typography sx={{ width: '140px' }}>
                <CurrencyFormat value={row.budget} decimalScale={2} fixedDecimalScale displayType="text" thousandSeparator prefix="E" />
            </Typography>
        )
    },
    {
        title: 'Contract PDF',
        field: 'pdf_document',
        render: (rowData) => rowData.pdf_document && <Chip label="View" variant="outlined" onClick={() => pdfView(rowData.pdf_document)} />
    },
    { title: 'Date Created', field: 'created_at' }
];
export const detailPanelColumn = [
    { title: 'Contract Type', field: 'type' },
    { title: 'Contract Budget', field: 'budget' },
    {
        title: 'Contract PDF',
        field: 'pdf_document',
        render: (rowData) => rowData.pdf_document && <Chip label="View" variant="outlined" onClick={() => pdfView(rowData.pdf_document)} />
    },
    { title: 'Date Created', field: 'created_at', render: (row) => fmtDate(row.created_at) }
];

export const initials = (fname = '', lname = '') => `${fname[0] ?? ''}${lname[0] ?? ''}`.toUpperCase();

export const toFormData = (obj) => {
    const fd = new FormData();
    Object.entries(obj).forEach(([k, v]) => {
        if (v instanceof File) fd.append(k, v);
        else if (v != null) fd.append(k, String(v));
    });
    return fd;
};

export const STEPS = [
    {
        num: 1,
        key: 'step1_complete',
        label: 'Ministry Instruction',
        short: 'Ministry Instr.',
        icon: '📋',
        desc: 'Upload scanned ministry instruction with fund & sector'
    },
    {
        num: 2,
        key: 'step2_complete',
        label: 'Tender Advert & Evaluation',
        short: 'Tender Docs',
        icon: '📢',
        desc: 'Upload tender advert and evaluation report, add project number'
    },
    {
        num: 3,
        key: 'step3_complete',
        label: 'Communication to Client',
        short: 'Client Comm.',
        icon: '📩',
        desc: 'Upload scanned communication letter to client'
    },
    {
        num: 4,
        key: 'step4_complete',
        label: 'Commencement Order',
        short: 'Commenc. Order',
        icon: '🗂️',
        desc: 'Upload letter of award, priced bill and contract (all 3 required)'
    },
    {
        num: 5,
        key: 'step5_complete',
        label: 'Claims Submission',
        short: 'Claims',
        icon: '📑',
        desc: 'Submit progress claims, retention claim and optional variation claim'
    },
    {
        num: 6,
        key: 'step6_complete',
        label: 'Certificates',
        short: 'Certificates',
        icon: '🏅',
        desc: 'Upload project certificates (interim, completion, etc.)'
    }
];

// ─── Status display config ────────────────────────────────────────────────────
export const STATUS_CFG = {
    incomplete: { color: 'default', label: 'Incomplete' },
    pending: { color: 'warning', label: 'Pending Review' },
    processing: { color: 'info', label: 'Processing' },
    completed: { color: 'success', label: 'Completed' },
    on_hold: { color: 'error', label: 'On Hold' },
    draft: { color: 'default', label: 'Draft' },
    pending_review: { color: 'warning', label: 'Pending Review' },
    qs_approved: { color: 'success', label: 'QS Approved' },
    returned: { color: 'error', label: 'Returned' },
    procurement_processing: { color: 'info', label: 'With Procurement' }
};

// ─── Reference data ───────────────────────────────────────────────────────────
export const FUNDS = ['Admin', 'CDSF', 'Education', 'Health', 'RDF', 'Other Ministries'];
export const SECTORS = [
    'Education',
    'Health',
    'Infrastructure',
    'Water & Sanitation',
    'Agriculture',
    'Social Welfare',
    'Housing',
    'Transport',
    'Other'
];
export const REGIONS = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];
export const MINISTRIES = [
    'Ministry of Education',
    'Ministry of Health',
    'Ministry of Public Works',
    'Ministry of Housing',
    'Ministry of Agriculture',
    'Ministry of Finance',
    'Ministry of Transport',
    'Ministry of Water'
];

export const CERTIFICATE_TYPES = [
    { value: 'interim', label: 'Interim Certificate' },
    { value: 'completion', label: 'Completion Certificate' },
    { value: 'final_account', label: 'Final Account Certificate' },
    { value: 'variation', label: 'Variation Certificate' },
    { value: 'retention_release', label: 'Retention Release Certificate' }
];
